import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/** The key of the city property the stores read the delivery minimum from (`CityProperty.key`). */
export const CITY_INFO_KEY = 'city-info';

/** The JSON of the city property `city-info`; only `deliveryMinOrderPrice` is known to the screen, any other key must survive a save. */
export interface CityInfoValue {
  deliveryMinOrderPrice?: number | null;
  [key: string]: unknown;
}

/** The property as the backend gives it (`GET /api/vn/cities/{id}/properties?key=city-info`). */
export interface CityInfoProperty {
  id: number;
  enabled: boolean;
  key: string;
  value: CityInfoValue;
}

/**
 * The new JSON of `city-info` after the minimum is set: the whole current JSON, only `deliveryMinOrderPrice` replaced (an empty or zero minimum is written as 0 = no limit).
 * `POST …/properties` replaces the value as a whole and `CityProperty` drops the keys it does not know, so a save from a form of one field must carry every other key back.
 */
export function mergeCityInfo(current: Readonly<CityInfoValue> | null | undefined, deliveryMinOrderPrice: number | null | undefined): CityInfoValue {
  const price = typeof deliveryMinOrderPrice === 'number' && isFinite(deliveryMinOrderPrice) && deliveryMinOrderPrice > 0 ? deliveryMinOrderPrice : 0;
  return {...(current ?? {}), deliveryMinOrderPrice: price};
}

/** The minimum order sum for delivery of a city: the field «Минимальная сумма доставки» of the city card instead of the raw JSON of the property. */
@Injectable({
  providedIn: 'root'
})
export class HxCityInfoService {
  constructor(
    private http: HttpClient,
  ) {
  }

  /** `null` when the city has no `city-info` property yet. */
  async getCityInfo(cityId: number): Promise<CityInfoProperty | null> {
    const property = await firstValueFrom(this.http.get<CityInfoProperty | null>(`/api/vn/cities/${cityId}/properties`, {params: {key: CITY_INFO_KEY}}));
    return property ?? null;
  }

  /** The current minimum; empty when there is none (no property, no key or 0). */
  async getDeliveryMinOrderPrice(cityId: number): Promise<number | null> {
    const info = await this.getCityInfo(cityId);
    const price = info?.value?.deliveryMinOrderPrice;
    return typeof price === 'number' && price > 0 ? price : null;
  }

  /**
   * Writes the minimum: reads the property again, replaces only `deliveryMinOrderPrice` and writes the whole JSON back, `enabled` (the stores read enabled properties only).
   * Nothing is written when there is no property and no minimum to set.
   */
  async saveDeliveryMinOrderPrice(cityId: number, deliveryMinOrderPrice: number | null | undefined): Promise<void> {
    const current = await this.getCityInfo(cityId);
    const value = mergeCityInfo(current?.value, deliveryMinOrderPrice);
    if (!current && !value.deliveryMinOrderPrice) {
      return;
    }
    await firstValueFrom(this.http.post<{ id: number }>(`/api/vn/cities/${cityId}/properties`, {key: CITY_INFO_KEY, enabled: true, value}));
  }
}
